import { acceptHMRUpdate, defineStore } from 'pinia'
import { reactive } from 'vue'
/* import { useToast } from 'primevue/usetoast' */

import type { WhatsappOperatorInsightsResponse } from '@/services/whatsapp-ai/interfaces/whatsapp-ai-analysis.interface'
import { useWhatsappAiAnalysisService } from '@/services/whatsapp-ai/useWhatsappAiAnalysisService'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import {
  AI_DISABLED_MESSAGE,
  /* AI_DISABLED_TOAST_MESSAGE, */
  isAiDisabledError,
} from '@/utils/aiDisabledError'

interface WhatsappAiInsightState {
  summary: string | null
  suggestions: string[]
  processedUntil: number
  messageCount: number
  hydrated: boolean
  loading: boolean
  error: string | null
  lastRequestAt: number
}

const MIN_MESSAGES_FOR_GENERATION = 5
const DEBOUNCE_MS = 2000
const COOLDOWN_MS = 20000

const createInsightState = (): WhatsappAiInsightState => ({
  summary: null,
  suggestions: [],
  processedUntil: 0,
  messageCount: 0,
  hydrated: false,
  loading: false,
  error: null,
  lastRequestAt: 0,
})

const asSuggestions = (value: unknown): string[] =>
  Array.isArray(value)
    ? value
        .filter((item): item is string => typeof item === 'string' && Boolean(item.trim()))
        .map((item) => item.trim())
        .slice(0, 3)
    : []

export const useWhatsappAiStore = defineStore('whatsapp-ai', () => {
  const workspace = useWorkspaceStore()
  /* const toast = useToast() */
  const insights = reactive<Record<string, WhatsappAiInsightState>>({})
  const timers = new Map<string, number>()
  const requestPromises = new Map<string, Promise<void>>()

  const stateFor = (chatId: string): WhatsappAiInsightState => {
    insights[chatId] ??= createInsightState()
    return insights[chatId]
  }

  const applyResponse = (
    state: WhatsappAiInsightState,
    response: WhatsappOperatorInsightsResponse,
  ) => {
    state.summary = response.summary?.trim() || state.summary
    state.suggestions = asSuggestions(response.suggestions)
    state.processedUntil = response.processed_until ?? state.processedUntil
    state.messageCount = response.message_count
    state.error = null
  }

  const refreshInsights = async (
    chatId: string,
    messageCount: number,
    eventType?: string | null,
  ) => {
    const activeRequest = requestPromises.get(chatId)
    if (activeRequest) return activeRequest

    const request = (async () => {
      const state = stateFor(chatId)
      state.loading = true
      state.error = null
      state.lastRequestAt = Date.now()

      try {
        const { generateOperatorInsights, getOperatorInsights } = useWhatsappAiAnalysisService()
        const current = await getOperatorInsights(chatId)
        applyResponse(state, current)

        const hasInsights = Boolean(current.summary?.trim() || current.suggestions.length)
        const shouldTryGenerate =
          Boolean(eventType) ||
          (!hasInsights && messageCount >= MIN_MESSAGES_FOR_GENERATION) ||
          current.pending_messages >= MIN_MESSAGES_FOR_GENERATION

        if (shouldTryGenerate) {
          const generated = await generateOperatorInsights(chatId, { event_type: eventType })
          applyResponse(state, generated)
        } else if (!hasInsights && messageCount < MIN_MESSAGES_FOR_GENERATION) {
          state.summary = null
          state.suggestions = []
          state.error = 'La interacción es muy corta para generar un resumen o sugerencias.'
        }
      } catch (error) {
        if (isAiDisabledError(error)) {
          workspace.setUseIa(false)
          /* toast.add({
            severity: 'warn',
            summary: 'IA deshabilitada',
            detail: AI_DISABLED_TOAST_MESSAGE,
            life: 5000,
          }) */
          state.summary = null
          state.suggestions = []
          state.error = AI_DISABLED_MESSAGE
        } else {
          state.error = error instanceof Error ? error.message : 'No se pudo cargar la IA del chat.'
        }
      } finally {
        state.loading = false
        state.hydrated = true
        requestPromises.delete(chatId)
      }
    })()

    requestPromises.set(chatId, request)
    return request
  }

  const scheduleInsights = (chatId: string, messageCount: number, eventType?: string | null) => {
    const existingTimer = timers.get(chatId)
    if (existingTimer !== undefined) window.clearTimeout(existingTimer)

    const state = stateFor(chatId)
    const cooldownRemaining = Math.max(COOLDOWN_MS - (Date.now() - state.lastRequestAt), 0)
    const delay = eventType ? 0 : Math.max(DEBOUNCE_MS, cooldownRemaining)

    const timer = window.setTimeout(() => {
      timers.delete(chatId)
      void refreshInsights(chatId, messageCount, eventType)
    }, delay)
    timers.set(chatId, timer)
  }

  const cancelScheduledInsights = (chatId: string) => {
    const timer = timers.get(chatId)
    if (timer === undefined) return
    window.clearTimeout(timer)
    timers.delete(chatId)
  }

  const dispose = () => {
    for (const timer of timers.values()) window.clearTimeout(timer)
    timers.clear()
    requestPromises.clear()
  }

  const clearInsights = () => {
    dispose()
    Object.keys(insights).forEach((chatId) => {
      delete insights[chatId]
    })
  }

  return {
    cancelScheduledInsights,
    clearInsights,
    dispose,
    insights,
    refreshInsights,
    scheduleInsights,
    stateFor,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useWhatsappAiStore, import.meta.hot))
}
